import React, { useEffect, useRef } from "react";
import "jquery/dist/jquery.min.js";
import jsZip from "jszip";
import "datatables.net-dt/js/dataTables.dataTables";
import "datatables.net-dt/css/jquery.dataTables.min.css";
import "datatables.net-buttons/js/dataTables.buttons.js";
import "datatables.net-buttons/js/buttons.colVis.js";
import "datatables.net-buttons/js/buttons.html5.js";
import "../../common/data-table/data-table.css";
import $ from "jquery";

window.JSZip = jsZip;

const PRINT_MAX_ROWS = 500;
const TABLE_ID = "transaction-list-table";

const escapeHtml = (value) =>
    String(value ?? "—")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const AMOUNT_COL_INDEX = 7; // 0-based index of the "Amount (₦)" column in tableRows

const computeTotalAmount = (rows) => {
    let total = 0;
    for (const row of rows) {
        const raw = String(row[AMOUNT_COL_INDEX] ?? "").replace(/[^0-9.]/g, "");
        const n = parseFloat(raw);
        if (!isNaN(n)) total += n;
    }
    return total;
};

const fmtPrintCurrency = (n) =>
    `\u20a6${Number(n).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const buildPrintDocument = (headers, rows, title, totalCount) => {
    const truncated = totalCount > PRINT_MAX_ROWS;
    const printRows = rows.slice(0, PRINT_MAX_ROWS);
    const today = new Date().toLocaleDateString("en-GB");
    const totalAmount = computeTotalAmount(printRows);
    const colSpan = headers.length;

    const head = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("");

    // Build body in chunks to avoid creating one huge string concat
    const bodyParts = [];
    for (let i = 0; i < printRows.length; i++) {
        const row = printRows[i];
        bodyParts.push(
            `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`
        );
    }

    // Total footer — spans all columns; amount is right-aligned in the last data column
    const totalFooter = `
        <tfoot>
            <tr class="total-row">
                <td colspan="${AMOUNT_COL_INDEX}" class="total-label">TOTAL AMOUNT</td>
                <td class="total-value">${fmtPrintCurrency(totalAmount)}</td>
                <td colspan="${colSpan - AMOUNT_COL_INDEX - 1}"></td>
            </tr>
        </tfoot>`;

    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>${title}</title>
    <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a2e; margin: 16px; }
        h1 { font-size: 18px; margin: 0 0 4px; text-align: center; }
        p  { font-size: 11px; margin: 0 0 12px; text-align: center; color: #555; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #206bc4; color: #fff; padding: 5px 6px; text-align: left; font-size: 10px; }
        td { padding: 4px 6px; border-bottom: 1px solid #e9ecef; font-size: 10px; vertical-align: top; }
        tr:nth-child(even) td { background: #f8f9fc; }
        .note { margin-top: 10px; font-size: 10px; color: #b45309; text-align: center; }
        .total-row td { border-top: 2px solid #206bc4; background: #eaf2fb !important; font-weight: bold; }
        .total-label { text-align: right; color: #206bc4; font-size: 10px; letter-spacing: .05em; }
        .total-value { color: #2fb344; font-size: 11px; white-space: nowrap; }
    </style>
</head>
<body>
    <h1>ODFeL — ${title}</h1>
    <p>Generated: ${today} | Records: ${totalCount.toLocaleString()}</p>
    <table>
        <thead><tr>${head}</tr></thead>
        <tbody>${bodyParts.join("")}</tbody>
        ${totalFooter}
    </table>
    ${
        truncated
            ? `<p class="note">Showing first ${PRINT_MAX_ROWS.toLocaleString()} of ${totalCount.toLocaleString()} records. Use Excel/CSV export for the full list.</p>`
            : ""
    }
</body>
</html>`;
};

/**
 * Opens a print window using a Blob URL instead of document.write().
 *
 * document.write() passes the entire HTML string through Chrome's IPC layer.
 * For large datasets this exceeds Chrome's message size limit, causing the
 * renderer process to be killed with RESULT_CODE_KILLED_BAD_MESSAGE.
 *
 * Blob URLs are read from memory directly — no IPC size limit applies.
 */
const openPrintWindow = (headers, rows, title) => {
    const html = buildPrintDocument(headers, rows, title, rows.length);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);

    const win = window.open(blobUrl, "_blank", "noopener,noreferrer,width=1024,height=768");
    if (!win) {
        URL.revokeObjectURL(blobUrl);
        window.alert("Please allow pop-ups to print this report.");
        return;
    }

    // Revoke after the window has had time to load and print
    setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
    }, 30_000);
};

export default function TransactionDataTable({ headers, rows, title }) {
    const tableRef = useRef(null);
    const rowsRef = useRef(rows);
    const initializedRef = useRef(false);

    // Always keep rowsRef current for the print action closure
    rowsRef.current = rows;

    // ── Init / destroy ──────────────────────────────────────────────────────
    useEffect(() => {
        const el = tableRef.current;
        if (!el || initializedRef.current) return;

        const $table = $(el);
        initializedRef.current = true;

        $table.DataTable({
            data: rows,
            columns: headers.map((label, index) => ({
                title: label,
                data: index,
                defaultContent: "—",
            })),
            // ── Performance knobs ───────────────────────────────────────────
            deferRender: true,       // only render DOM for visible rows
            orderClasses: false,     // skip per-cell CSS class toggling on sort
            stateSave: false,        // no localStorage read/write on every action
            // ───────────────────────────────────────────────────────────────
            pagingType: "full_numbers",
            pageLength: 25,
            processing: true,
            order: [[1, "desc"]],
            dom: "Bfrtip",
            buttons: [
                {
                    extend: "pageLength",
                    className: "btn btn-secondary bg-secondary",
                },
                {
                    extend: "copy",
                    className: "btn btn-secondary bg-secondary",
                    exportOptions: { modifier: { search: "applied", order: "applied" } },
                },
                {
                    extend: "csv",
                    className: "btn btn-secondary bg-secondary",
                    exportOptions: { modifier: { search: "applied", order: "applied" } },
                },
                {
                    extend: "excelHtml5",
                    className: "btn btn-secondary bg-secondary",
                    exportOptions: { modifier: { search: "applied", order: "applied" } },
                },
                {
                    text: "Print",
                    className: "btn btn-secondary bg-secondary",
                    action: () => {
                        const dt = $table.DataTable();
                        const data = dt
                            .rows({ search: "applied", order: "applied" })
                            .data()
                            .toArray();
                        openPrintWindow(
                            headers,
                            data.length ? data : rowsRef.current,
                            title
                        );
                    },
                },
            ],
            lengthMenu: [
                [10, 25, 50, 100, 200],
                [10, 25, 50, 100, 200],
            ],
            columnDefs: [
                {
                    targets: 0,
                    orderable: false,
                    searchable: false,
                    width: "40px",
                    render: (_data, _type, _row, meta) => meta.row + 1,
                },
            ],
            language: {
                emptyTable: "No transactions to display.",
                processing:
                    '<span class="spinner-border spinner-border-sm me-2"></span>Loading…',
            },
        });

        return () => {
            if ($.fn.DataTable.isDataTable(`#${TABLE_ID}`)) {
                try {
                    $table.DataTable().destroy(false);
                } catch (_) {
                    // already cleaned up
                }
            }
            initializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once — row updates handled by the effect below

    // ── Row updates (filter changes) ────────────────────────────────────────
    useEffect(() => {
        if (!initializedRef.current) return;
        const el = tableRef.current;
        if (!el) return;

        const selector = `#${TABLE_ID}`;
        if (!$.fn.DataTable.isDataTable(selector)) return;

        const dt = $(el).DataTable();
        dt.clear();
        if (rows.length) {
            dt.rows.add(rows);
        }
        dt.draw(false); // false = stay on current page
    }, [rows]);

    return (
        <table
            ref={tableRef}
            id={TABLE_ID}
            className="table caption-top myTable align-items-center justify-content-center mb-0 w-100"
        />
    );
}
